import store from '@/store';
import router from '@/router';
import { reactive, onBeforeMount, watch } from '@vue/composition-api';

import { groupBannerModel, setBannerModel } from '@/models/banner/banner';
import useDate from '@/utils/hooks/useDate';
import usePagination from '@/views/components/pagination/usePagination';
import useModal from '@/@core/components/base-modal/hooks/useModal';
import useValidationModal from '@/@core/components/base-modal/hooks/useValidationModal';

import useFilterHeaderTable from '@/views/components/filter-header-table/useFilterHeaderTable';

import usePermissionByAccount from "@/constants/navigation/BOYNYKJMS2EH/permission-by-account"

export default function useSetBanner() {
  const { getPermissionModule } = usePermissionByAccount()
  const { resolveDateWithSeperator } = useDate();
  const { fetchSetBannerListModel, removeSetBannerModel } = setBannerModel();
  const { pagination } = usePagination();
  const { openModal, closeModal } = useModal();
  const { openDefaultErrorModal, openWarningModal } = useValidationModal();

  const {
    filters,
    searchValue,
    ordering,
    onSearch,
    sortingChanged,
  } = useFilterHeaderTable();

  const fields = [
    { key: 'seq', label: 'No.' },
    { key: 'set_name', label: 'Set Name', sortable: true },
    { key: 'description', label: 'Description' },
    { key: 'valid_from', label: 'Valid From' },
    { key: 'valid_through', label: 'Valid Through' },
    { key: 'updatetime', label: 'Latest Update' },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'actions', label: '' },
  ];

  const loading = reactive({
    tableData: false,
    api: false,
  });

  const setBannerActionList = reactive({
    edit: { label: 'Edit', value: 1, icon: 'EditIcon', display: true },
    assignGroup: {
      label: 'Assign Banner Group',
      value: 2,
      icon: 'AssignBannerGroupIcon',
      display: true,
    },
    delete: { label: 'Delete', value: 3, icon: 'DeleteIcon', display: true },
  });

  const setBannerTable = reactive({
    column: [],
    payload: [],
    filterColumn: [],
    filter: [],
    isCreate: false,
  });

  const assignBannerConfig = reactive({
    setBannerId: null,
    option: {
      group: [],
    },
  });

  const fetchSetBannerList = (loadingAction = true) => {
    if (loadingAction) {
      loading.tableData = true;
    }
    fetchSetBannerListModel.payload.Page = pagination.currentPage;
    fetchSetBannerListModel.payload.Limit = pagination.perPage;
    fetchSetBannerListModel.payload.Filters = filters;
    fetchSetBannerListModel.payload.Ordering = ordering.value;

    store
      .dispatch('api/apiRequest', fetchSetBannerListModel)
      .then((response) => {
        setBannerTable.payload = response.data.payload;
        pagination.rows = response.data.payload.length;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        loading.tableData = false;
      });
  };

  const removeSetBanner = (setBannerId) => {
    loading.api = true;
    removeSetBannerModel.payload.Set_Banner_Id = setBannerId;

    store
      .dispatch('api/apiRequest', removeSetBannerModel)
      .then((response) => {
        loading.api = false;
        fetchSetBannerList(false);
      })
      .catch((err) => {
        loading.api = false;
      });
  };

  const resolveBannerStatusLabel = (status) => {
    switch (status) {
      case true:
        return 'Active';
      case false:
        return 'Inactive';

      default:
        return 'Unknown';
    }
  };

  const resolveBannerAction = (type, itemData = null) => {
    switch (type) {
      case 1:
        if (itemData) {
          router.push(
            `/banner/set/Banner_Set/${itemData.set_banner_id}/info`
          );
        }
        return;
      case 2:
        openAssignModal(itemData);
        return;
      case 3:
        openRemoveWarningModal(itemData);
        return;
      default:
        return;
    }
  };

  const formModal = reactive({
    assignModal: false
  })

  const openAssignModal = (setBanner) => {
    formModal.assignModal = true
    assignBannerConfig.setBannerId = setBanner.set_banner_id;
    openModal();
  };

  const onPageChange = (page) => {
    pagination.currentPage = page;
  };

  const onSearchBanner = (searchInput) => {
    searchValue.query = searchInput;
    onSearch();
  };

  const openRemoveWarningModal = (setBanner) => {
    openWarningModal(
      'ลบ Banner Set',
      `ต้องการลบ ${setBanner.set_name} หรือไม่`,
      () => {
        removeSetBanner(setBanner.set_banner_id);
      }
    );
  };

  onBeforeMount(() => {
    fetchSetBannerList();
  });

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchSetBannerList();
      }
    },
    { deep: true }
  );

  watch(
    [filters, ordering],
    () => {
      pagination.currentPage = 1;

      fetchSetBannerList();
    },
    { deep: true }
  );

  return {
    fields,
    setBannerTable,
    setBannerActionList,
    pagination,
    ordering,
    loading,
    assignBannerConfig,
    resolveDateWithSeperator,
    resolveBannerStatusLabel,
    resolveBannerAction,
    onPageChange,
    onSearchBanner,
    sortingChanged,
    closeModal,
    openRemoveWarningModal,
    formModal,
    getPermissionModule
  };
}
